/**
 * Clients — раздел «Клиенты» (ADMIN_SPEC §6, design_handoff_02_clients).
 * Оркестратор: состояние фильтров + запрос (мок) + переключение
 * десктоп/мобилка. Разметка — в ClientsDesktop / ClientsMobile.
 *
 * Клик по строке/карточке → карточка клиента (drawer) — следующая задача;
 * сейчас `handleOpenClient` — заглушка с сохранённым контрактом.
 */

import {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';

import {type ClientListItem, clientsQueryOptions} from '@services/clients';
import {useBreakpoint} from '@hooks/useBreakpoint';

import {ClientCard} from './ClientCard';
import {type ClientsFilters, EMPTY_FILTERS, toRequest} from './Clients.utils';
import {ClientsDesktop} from './ClientsDesktop';
import {ClientsMobile} from './ClientsMobile';
import {CreateAccountModal} from './CreateAccountModal';

export const Clients = () => {
  const {isMobile} = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<ClientsFilters>(() => {
    const q = searchParams.get('q');
    const status = searchParams.get('status') as ClientsFilters['status'] | null;
    return {
      ...EMPTY_FILTERS,
      ...(q ? {search: q} : {}),
      ...(status ? {status} : {}),
    };
  });
  const [createOpen, setCreateOpen] = useState(() => searchParams.get('create') === '1');
  /** Открытая карточка: храним id, строку берём из СВЕЖЕГО списка после
      рефетчей; снапшот — фолбэк, если строка ушла с текущей страницы */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSnap, setSelectedSnap] = useState<ClientListItem | null>(null);
  const patch = (p: Partial<ClientsFilters>) => setFilters((f) => ({...f, ...p}));

  const req = useMemo(() => toRequest(filters), [filters]);
  const {data, isLoading} = useQuery(clientsQueryOptions(req));

  const rows = data?.list ?? [];
  const totalCount = data?.totalCount ?? 0;
  const pageSize = data?.pageSize ?? 25;

  const selected =
    selectedId === null ? null : (rows.find((c) => c.id === selectedId) ?? selectedSnap);

  const handleOpenClient = (id: string) => {
    setSelectedSnap(rows.find((c) => c.id === id) ?? null);
    setSelectedId(id);
  };
  const openCreate = () => setCreateOpen(true);

  return (
    <>
      {isMobile ? (
        <ClientsMobile
          filters={filters}
          patch={patch}
          rows={rows}
          totalCount={totalCount}
          isLoading={isLoading}
          onOpenClient={handleOpenClient}
          onCreate={openCreate}
        />
      ) : (
        <ClientsDesktop
          filters={filters}
          patch={patch}
          rows={rows}
          totalCount={totalCount}
          pageSize={pageSize}
          isLoading={isLoading}
          onOpenClient={handleOpenClient}
          onCreate={openCreate}
        />
      )}

      {/* Монтируем по открытию: модалка заводит состояние формы один
          раз, и без размонтирования второй заход показывал бы данные
          прошлого клиента, его «номер уже занят» и заблокированную
          кнопку создания. */}
      {createOpen && <CreateAccountModal open onOpenChange={setCreateOpen} />}
      <ClientCard client={selected} onClose={() => setSelectedId(null)} />
    </>
  );
};
